import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Banknote,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Eye,
  LayoutDashboard,
  Paperclip,
  Pencil,
  Plus,
  Receipt,
  Search,
  Settings,
  Trash2,
  Truck,
} from "lucide-react";
import { toast } from "react-toastify";
import Cleave from "cleave.js/react";
import ReactQuill from "react-quill";
import Select, { type StylesConfig } from "react-select";
import { ModalBase } from "@/components/ModalBase/ModalBase";
import FornecedoresEmpresaServices from "@/services/FornecedoresEmpresaServices";
import {
  DeskProducaoCategoria,
  DeskProducaoData,
  DeskProducaoDashboard,
  DeskProducaoLancamento,
  DeskProducaoResumoItem,
  DeskProducaoStatus,
  ServiceDeskProducao,
} from "@/services/serviceDeskProducao";

export type ControleProducaoView =
  | "dashboard"
  | "lancamentos"
  | "fornecedores"
  | "pagamentos"
  | "configuracoes";

type ControleProducaoProps = {
  desk: {
    id: number;
  };
  activeView: ControleProducaoView;
  onChangeView: (view: ControleProducaoView) => void;
  isOpen?: boolean;
  onToggle?: () => void;
  onOpenMain?: () => void;
  onPrimaryAction?: (view: ControleProducaoView) => void;
  onRefreshTimeline?: () => void;
  actionRequest?: { view: ControleProducaoView; nonce: number } | null;
};

type FornecedorOption = {
  id: number;
  nome: string;
  name_fantasy?: string | null;
  documento?: string | null;
  tipo_servico?: string | null;
};

type FornecedorListItem = FornecedorOption & {
  total: number;
  quantidade_lancamentos: number;
};

type FornecedorDetalhe = FornecedorOption & {
  tipo?: "PF" | "PJ" | null;
  email?: string | null;
  telefone?: string | null;
  website?: string | null;
  endereco?: string | null;
  numero?: number | null;
  complemento?: string | null;
  bairro?: string | null;
  municipio?: string | null;
  uf?: string | null;
  cep?: string | null;
  descricao?: string | null;
};

type FornecedorModalMode = "create" | "view" | "edit";

type FornecedorFormState = {
  nome: string;
  name_fantasy: string;
  tipo: "PF" | "PJ";
  documento: string;
  email: string;
  telefone: string;
  tipo_servico: string;
  website: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
  uf: string;
  descricao: string;
};

type LancamentoFormState = {
  fornecedor_empresa_id: string;
  novo_fornecedor: boolean;
  fornecedor_nome: string;
  fornecedor_name_fantasy: string;
  fornecedor_documento: string;
  fornecedor_email: string;
  fornecedor_telefone: string;
  fornecedor_tipo_servico: string;
  descricao: string;
  categoria_id: string;
  centro_custo: string;
  departamento_responsavel: string;
  data_contratacao: string;
  data_vencimento: string;
  data_pagamento: string;
  valor_orcado: string;
  valor_aprovado: string;
  valor_final: string;
  forma_pagamento: string;
  parcelamento: string;
  numero_documento: string;
  status: DeskProducaoStatus;
  observacoes: string;
};

type ControleProducaoFiltersState = {
  status: string;
  fornecedor_empresa_id: string;
  categoria_id: string;
  periodo_inicio: string;
  periodo_fim: string;
};

type SelectOption = {
  value: string;
  label: string;
};

const MENU_ITEMS: Array<{
  id: ControleProducaoView;
  label: string;
  icon: typeof LayoutDashboard;
}> = [
  { id: "dashboard", label: "Visão geral", icon: LayoutDashboard },
  { id: "lancamentos", label: "Lançamentos", icon: Receipt },
  { id: "fornecedores", label: "Fornecedores", icon: Truck },
  { id: "pagamentos", label: "Pagamentos", icon: Banknote },
  { id: "configuracoes", label: "Configurações", icon: Settings },
];

const STATUS_LABELS: Record<DeskProducaoStatus, string> = {
  orcado: "Orçado",
  aprovado: "Aprovado",
  aguardando_pagamento: "Aguardando pagamento",
  pago: "Pago",
  cancelado: "Cancelado",
};

const STATUS_CLASS: Record<DeskProducaoStatus, string> = {
  orcado: "bg-azul-400/20 text-azul-400",
  aprovado: "bg-verde-300/20 text-verde-300",
  aguardando_pagamento: "bg-goon-amareloClaro/60 text-cinza-800",
  pago: "bg-verde-300/20 text-verde-300",
  cancelado: "bg-red-100 text-red-600",
};

const CHART_COLORS = [
  "#153DCE",
  "#7ADC2E",
  "#FFB020",
  "#9B51E0",
  "#00A3A3",
  "#EB5757",
  "#828282",
];

const selectStyles: StylesConfig<SelectOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderColor: state.isFocused ? "#153DCE" : "#D7D7D7",
    borderRadius: 8,
    boxShadow: "none",
    fontSize: 13,
    "&:hover": { borderColor: "#153DCE" },
  }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13 }),
  placeholder: (base) => ({ ...base, color: "#9CA3AF" }),
  singleValue: (base) => ({ ...base, color: "#1F1F1F" }),
};

const emptyForm: LancamentoFormState = {
  fornecedor_empresa_id: "",
  novo_fornecedor: false,
  fornecedor_nome: "",
  fornecedor_name_fantasy: "",
  fornecedor_documento: "",
  fornecedor_email: "",
  fornecedor_telefone: "",
  fornecedor_tipo_servico: "",
  descricao: "",
  categoria_id: "",
  centro_custo: "",
  departamento_responsavel: "",
  data_contratacao: "",
  data_vencimento: "",
  data_pagamento: "",
  valor_orcado: "",
  valor_aprovado: "",
  valor_final: "",
  forma_pagamento: "",
  parcelamento: "",
  numero_documento: "",
  status: "orcado",
  observacoes: "",
};

const emptyFornecedorForm: FornecedorFormState = {
  nome: "",
  name_fantasy: "",
  tipo: "PJ",
  documento: "",
  email: "",
  telefone: "",
  tipo_servico: "",
  website: "",
  cep: "",
  endereco: "",
  numero: "",
  complemento: "",
  bairro: "",
  municipio: "",
  uf: "",
  descricao: "",
};

function money(value?: number | string | null) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function percent(value?: number | null, maximumFractionDigits = 1) {
  return `${Number(value || 0).toLocaleString("pt-BR", {
    maximumFractionDigits,
  })}%`;
}

function dateLabel(value?: string | null) {
  if (!value) return "-";
  return new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString(
    "pt-BR",
  );
}

function dateInput(value?: string | null) {
  return value ? String(value).slice(0, 10) : "";
}

function normalizeCurrencyInput(value: string) {
  return value
    .replace(/^R\$\s?/, "")
    .replace(/\./g, "")
    .replace(",", ".");
}

function toCurrencyInputValue(value?: string | null) {
  if (!value) return "";
  return String(value).replace(".", ",");
}

function onlyDigits(value: string, maxLength: number) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, maxLength);
}

function formatCpfCnpj(value: string) {
  const digits = onlyDigits(value, 14);

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})$/, "$1.$2.$3-$4");
  }

  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})$/, "$1.$2.$3/$4-$5");
}

function formatBrazilPhone(value: string) {
  const digits = onlyDigits(value, 11);

  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d{1,4})$/, "$1-$2");
  }

  return digits
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d{1,4})$/, "$1-$2");
}

function effectiveValue(lancamento: DeskProducaoLancamento) {
  return Number(
    lancamento.valor_final ??
      lancamento.valor_aprovado ??
      lancamento.valor_orcado ??
      0,
  );
}

function buildFormData(
  payload: Record<string, unknown>,
  files?: FileList | null,
) {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, String(value));
    }
  });
  Array.from(files || []).forEach((file) => formData.append("anexos", file));
  return formData;
}

function fornecedorName(fornecedor?: FornecedorOption | null) {
  if (!fornecedor) return "-";
  return fornecedor.name_fantasy || fornecedor.nome || "-";
}

function toFornecedorOption(fornecedor: FornecedorOption): SelectOption {
  return {
    value: String(fornecedor.id),
    label: fornecedorName(fornecedor),
  };
}

function toCategoriaOption(categoria: DeskProducaoCategoria): SelectOption {
  return {
    value: String(categoria.id),
    label: categoria.nome,
  };
}

function findOption(options: SelectOption[], value: string) {
  return options.find((option) => option.value === value) || null;
}

function RequiredLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <span>
      {children}
      {required && <span className="ml-1 font-semibold text-red-500">*</span>}
    </span>
  );
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function getLancamentoForm(lancamento?: DeskProducaoLancamento | null) {
  if (!lancamento) return emptyForm;
  return {
    ...emptyForm,
    fornecedor_empresa_id: String(lancamento.fornecedor_empresa_id || ""),
    descricao: lancamento.descricao || "",
    categoria_id: String(lancamento.categoria_id || ""),
    centro_custo: lancamento.centro_custo || "",
    departamento_responsavel: lancamento.departamento_responsavel || "",
    data_contratacao: dateInput(lancamento.data_contratacao),
    data_vencimento: dateInput(lancamento.data_vencimento),
    data_pagamento: dateInput(lancamento.data_pagamento),
    valor_orcado: String(lancamento.valor_orcado ?? ""),
    valor_aprovado: String(lancamento.valor_aprovado ?? ""),
    valor_final: String(lancamento.valor_final ?? ""),
    forma_pagamento: lancamento.forma_pagamento || "",
    parcelamento: lancamento.parcelamento || "",
    numero_documento: lancamento.numero_documento || "",
    status: lancamento.status,
    observacoes: lancamento.observacoes || "",
  };
}

function AnexosResumo({ lancamento }: { lancamento: DeskProducaoLancamento }) {
  const anexos = lancamento.anexos || [];
  if (!anexos.length) return <span className="text-cinza-400">-</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {anexos.slice(0, 2).map((anexo) =>
        anexo.file?.path ? (
          <a
            key={anexo.id}
            href={anexo.file.path}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg border border-cinza-200 px-2 py-1 text-[11px] text-azul-400 hover:bg-azul-400/10"
            title={anexo.file.name}
          >
            <Paperclip className="h-3 w-3" />
            {anexo.tipo === "comprovante" ? "Comprovante" : "Documento"}
          </a>
        ) : null,
      )}
      {anexos.length > 2 && (
        <span className="rounded-lg bg-cinza-100 px-2 py-1 text-[11px] text-cinza-500">
          +{anexos.length - 2}
        </span>
      )}
    </div>
  );
}

export function ControleProducaoCard({
  activeView,
  onChangeView,
  isOpen = false,
  onToggle,
  onOpenMain,
  onPrimaryAction,
}: ControleProducaoProps) {
  const primaryAction: Record<ControleProducaoView, string> = {
    dashboard: "Novo lançamento",
    lancamentos: "Novo lançamento",
    fornecedores: "Adicionar fornecedor",
    pagamentos: "Registrar pagamento",
    configuracoes: "Nova categoria",
  };

  function handleSelect(view: ControleProducaoView) {
    onChangeView(view);
    onOpenMain?.();
  }

  return (
    <div
      className={`sm:max-w-72 w-[95vw] overflow-hidden bg-white rounded-3xl shadow-xl text-sm transition-all duration-300 ease-out ${
        isOpen ? "p-4 sm:p-6" : "p-4 sm:p-5"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-cinza-800">
            Controle de Produção
          </p>
          <p className="text-xs text-cinza-500">Custos e fornecedores</p>
        </div>
        <div className="flex items-center gap-2">
          <CircleDollarSign className="h-5 w-5 text-azul-400" />
          <button
            type="button"
            onClick={onToggle}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-azul-400/20 text-azul-400 transition hover:bg-azul-400 hover:text-white"
            title={isOpen ? "Recolher controle" : "Abrir controle"}
          >
            {isOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      <div
        className={`grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out ${
          isOpen
            ? "mt-4 grid-rows-[1fr] opacity-100"
            : "mt-0 grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <button
            type="button"
            onClick={() => onPrimaryAction?.(activeView)}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-azul-400 px-3 py-2 text-xs font-medium text-white transition hover:bg-azul-400/90"
          >
            <Plus className="h-4 w-4" />
            {primaryAction[activeView]}
          </button>

          <div className="mt-4 space-y-1">
            {MENU_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = activeView === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSelect(item.id)}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium transition ${
                    isActive
                      ? "bg-azul-400/10 text-azul-400"
                      : "text-cinza-600 hover:bg-cinza-100"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ControleProducaoContent({
  desk,
  activeView,
  onChangeView,
  onRefreshTimeline,
  actionRequest,
}: ControleProducaoProps) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ControleProducaoFiltersState>({
    status: "",
    fornecedor_empresa_id: "",
    categoria_id: "",
    periodo_inicio: "",
    periodo_fim: "",
  });
  const [searchFornecedor, setSearchFornecedor] = useState("");
  const [editingLancamento, setEditingLancamento] = useState<
    DeskProducaoLancamento | null | undefined
  >(undefined);
  const [payingLancamento, setPayingLancamento] =
    useState<DeskProducaoLancamento | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [fornecedorModal, setFornecedorModal] = useState<{
    mode: FornecedorModalMode;
    fornecedorId?: number;
  } | null>(null);
  const [fornecedorToDelete, setFornecedorToDelete] =
    useState<FornecedorListItem | null>(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const resumoQueryKey = ["desk-producao", desk?.id, filters];
  const { data, isLoading, refetch } = useQuery({
    queryKey: resumoQueryKey,
    queryFn: async () =>
      ServiceDeskProducao.getResumo(desk.id, {
        status: filters.status,
        fornecedor_empresa_id: filters.fornecedor_empresa_id,
        categoria_id: filters.categoria_id,
        periodo_inicio: filters.periodo_inicio,
        periodo_fim: filters.periodo_fim,
      }),
    enabled: Boolean(desk?.id),
    retry: 2,
    staleTime: 1000 * 60 * 2,
  });

  const fornecedoresResumoQuery = useQuery({
    queryKey: ["desk-producao-fornecedores", desk?.id],
    queryFn: async () => ServiceDeskProducao.getResumo(desk.id),
    enabled: Boolean(desk?.id) && activeView === "fornecedores",
    retry: 2,
    staleTime: 1000 * 60 * 2,
  });

  const fornecedoresQuery = useQuery({
    queryKey: ["fornecedores-empresa-controle-producao", searchFornecedor],
    queryFn: async () =>
      FornecedoresEmpresaServices.getListagemFornecedores(
        1,
        100,
        searchFornecedor,
      ),
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  const todosFornecedoresQuery = useQuery({
    queryKey: ["fornecedores-empresa-controle-producao-todos"],
    queryFn: async () =>
      FornecedoresEmpresaServices.getListagemFornecedores(1, 9999999999),
    enabled: activeView === "fornecedores",
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  const resumo = (data?.data as { data?: DeskProducaoData } | undefined)?.data;
  const resumoFornecedores = (
    fornecedoresResumoQuery.data?.data as
      | { data?: DeskProducaoData }
      | undefined
  )?.data;
  const fornecedores: FornecedorOption[] =
    fornecedoresQuery.data?.data?.data || [];
  const todosFornecedores = useMemo<FornecedorOption[]>(
    () => todosFornecedoresQuery.data?.data?.data || [],
    [todosFornecedoresQuery.data?.data?.data],
  );
  const categorias = useMemo<DeskProducaoCategoria[]>(
    () => resumo?.categorias || [],
    [resumo?.categorias],
  );
  const lancamentos = useMemo<DeskProducaoLancamento[]>(
    () => resumo?.lancamentos || [],
    [resumo?.lancamentos],
  );
  const pagamentos = useMemo<DeskProducaoLancamento[]>(
    () => resumo?.pagamentos || [],
    [resumo?.pagamentos],
  );
  const lancamentosSemFiltro = useMemo<DeskProducaoLancamento[]>(
    () => resumoFornecedores?.lancamentos || [],
    [resumoFornecedores?.lancamentos],
  );
  const dashboard = resumo?.dashboard;

  const fornecedoresComVinculo = useMemo<FornecedorListItem[]>(() => {
    const map = new Map<
      number,
      { total: number; quantidade_lancamentos: number }
    >();
    lancamentosSemFiltro.forEach((lancamento) => {
      if (!lancamento.fornecedor) return;
      const current = map.get(lancamento.fornecedor.id) || {
        total: 0,
        quantidade_lancamentos: 0,
      };
      current.total += effectiveValue(lancamento);
      current.quantidade_lancamentos += 1;
      map.set(lancamento.fornecedor.id, current);
    });

    return todosFornecedores
      .map((fornecedor) => ({
        ...fornecedor,
        total: map.get(fornecedor.id)?.total || 0,
        quantidade_lancamentos:
          map.get(fornecedor.id)?.quantidade_lancamentos || 0,
      }))
      .sort((a, b) => {
        if (a.quantidade_lancamentos !== b.quantidade_lancamentos) {
          return b.quantidade_lancamentos - a.quantidade_lancamentos;
        }
        return fornecedorName(a).localeCompare(fornecedorName(b), "pt-BR");
      });
  }, [lancamentosSemFiltro, todosFornecedores]);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["desk-producao", desk?.id] });
    queryClient.invalidateQueries({
      queryKey: ["desk-producao-fornecedores", desk?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ["fornecedores-empresa-controle-producao"],
    });
    queryClient.invalidateQueries({
      queryKey: ["fornecedores-empresa-controle-producao-todos"],
    });
    queryClient.invalidateQueries({ queryKey: ["fornecedor-empresa"] });
    refetch();
    onRefreshTimeline?.();
  }

  useEffect(() => {
    if (!actionRequest) return;

    if (actionRequest.view === "fornecedores") {
      setFornecedorModal({ mode: "create" });
      return;
    }

    if (actionRequest.view === "pagamentos") {
      setPaymentModalOpen(true);
      setPayingLancamento(null);
      return;
    }

    if (actionRequest.view === "configuracoes") {
      setCategoryModalOpen(true);
      return;
    }

    setEditingLancamento(null);
    onChangeView("lancamentos");
  }, [actionRequest, onChangeView]);

  const deleteMutation = useMutation({
    mutationFn: (lancamentoId: number) =>
      ServiceDeskProducao.deleteLancamento(desk.id, lancamentoId),
    onSuccess: (res) => {
      if (res?.status === 200) {
        toast.success("Lançamento removido com sucesso");
        invalidate();
      } else {
        toast.error(res?.data?.msg || "Erro ao remover lançamento");
      }
    },
  });

  const deleteFornecedorMutation = useMutation({
    mutationFn: (fornecedorId: number) =>
      FornecedoresEmpresaServices.deleteFornecedor(fornecedorId),
    onSuccess: (res) => {
      if (res?.status === 200) {
        setFornecedorToDelete(null);
        invalidate();
      }
    },
  });

  const updateOrcamentoMutation = useMutation({
    mutationFn: (payload: {
      valor: string;
      incentivo_percentual: string;
    }) => ServiceDeskProducao.updateOrcamento(desk.id, payload),
    onSuccess: (res) => {
      if (res?.status === 200) {
        toast.success("Orçamento atualizado com sucesso");
        invalidate();
      } else {
        toast.error(res?.data?.msg || "Erro ao atualizar orçamento");
      }
    },
  });

  if (isLoading) {
    return (
      <div className="rounded-lg border border-cinza-200 bg-white p-6 text-sm text-cinza-500">
        Carregando controle de produção...
      </div>
    );
  }

  if (data && data.status !== 200) {
    return (
      <div className="rounded-lg border border-cinza-200 bg-white p-6">
        <p className="text-sm font-medium text-cinza-800">
          Não foi possível carregar o controle de produção.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 rounded-lg bg-azul-400 px-3 py-2 text-xs font-medium text-white"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xl font-semibold text-cinza-800">
            Controle de Produção
          </p>
          <p className="text-sm text-cinza-500">
            Custos, fornecedores e pagamentos desta Desk.
          </p>
        </div>
      </div>

      {(activeView === "lancamentos" || activeView === "pagamentos") && (
        <Filters
          filters={filters}
          setFilters={setFilters}
          fornecedores={fornecedores}
          categorias={categorias}
          setSearchFornecedor={setSearchFornecedor}
        />
      )}

      {activeView === "dashboard" && (
        <Dashboard
          dashboard={dashboard}
          lancamentos={lancamentos}
          onOpenLancamentos={() => onChangeView("lancamentos")}
          onSaveOrcamento={(payload) =>
            updateOrcamentoMutation.mutateAsync(payload)
          }
          isSavingOrcamento={updateOrcamentoMutation.isPending}
        />
      )}
      {activeView === "lancamentos" && (
        <LancamentosTable
          lancamentos={lancamentos}
          onEdit={setEditingLancamento}
          onDelete={(id) => deleteMutation.mutate(id)}
          onPay={(lancamento) => {
            setPayingLancamento(lancamento);
            setPaymentModalOpen(true);
          }}
        />
      )}
      {activeView === "fornecedores" &&
      (todosFornecedoresQuery.isLoading || fornecedoresResumoQuery.isLoading) ? (
        <div className="rounded-lg border border-cinza-200 bg-white p-6 text-center text-sm text-cinza-500">
          Carregando fornecedores...
        </div>
      ) : activeView === "fornecedores" ? (
        <FornecedoresPanel
          fornecedores={fornecedoresComVinculo}
          onView={(fornecedor) =>
            setFornecedorModal({ mode: "view", fornecedorId: fornecedor.id })
          }
          onEdit={(fornecedor) =>
            setFornecedorModal({ mode: "edit", fornecedorId: fornecedor.id })
          }
          onDelete={(fornecedor) => {
            if (fornecedor.quantidade_lancamentos > 0) {
              toast.warning(
                `Este fornecedor possui ${fornecedor.quantidade_lancamentos} lançamento(s) nesta Desk e não pode ser excluído.`,
              );
              return;
            }
            setFornecedorToDelete(fornecedor);
          }}
        />
      ) : null}
      {activeView === "pagamentos" && (
        <PagamentosTable
          pagamentos={pagamentos}
          onPay={(lancamento) => {
            setPayingLancamento(lancamento);
            setPaymentModalOpen(true);
          }}
        />
      )}
      {activeView === "configuracoes" && (
        <CategoriasPanel
          categorias={categorias}
          onCreate={() => setCategoryModalOpen(true)}
        />
      )}

      <LancamentoModal
        deskId={desk.id}
        open={activeView === "lancamentos" && editingLancamento !== undefined}
        lancamento={editingLancamento}
        categorias={categorias}
        fornecedores={fornecedores}
        setSearchFornecedor={setSearchFornecedor}
        onClose={() => setEditingLancamento(undefined)}
        onSaved={invalidate}
      />

      <PagamentoModal
        deskId={desk.id}
        lancamento={payingLancamento}
        lancamentos={pagamentos}
        open={paymentModalOpen}
        onClose={() => setPayingLancamento(null)}
        onOpenChange={setPaymentModalOpen}
        onSaved={invalidate}
      />

      <FornecedorModal
        open={fornecedorModal !== null}
        mode={fornecedorModal?.mode || "create"}
        fornecedorId={fornecedorModal?.fornecedorId}
        onModeChange={(mode) =>
          setFornecedorModal((current) =>
            current ? { ...current, mode } : current,
          )
        }
        onClose={() => setFornecedorModal(null)}
        onSaved={invalidate}
        categorias={categorias}
      />

      <FornecedorDeleteModal
        fornecedor={fornecedorToDelete}
        isDeleting={deleteFornecedorMutation.isPending}
        onClose={() => setFornecedorToDelete(null)}
        onConfirm={(fornecedorId) =>
          deleteFornecedorMutation.mutate(fornecedorId)
        }
      />

      <CategoriaModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        onSaved={invalidate}
      />
    </div>
  );
}

function Filters({
  filters,
  setFilters,
  fornecedores,
  categorias,
  setSearchFornecedor,
}: {
  filters: ControleProducaoFiltersState;
  setFilters: Dispatch<SetStateAction<ControleProducaoFiltersState>>;
  fornecedores: FornecedorOption[];
  categorias: DeskProducaoCategoria[];
  setSearchFornecedor: (value: string) => void;
}) {
  const statusOptions: SelectOption[] = [
    { value: "", label: "Todos" },
    { value: "pendente", label: "Pendente" },
    { value: "vencido", label: "Vencido" },
    ...Object.entries(STATUS_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  ];
  const fornecedorOptions = [
    { value: "", label: "Todos" },
    ...fornecedores.map(toFornecedorOption),
  ];
  const categoriaOptions = [
    { value: "", label: "Todas" },
    ...categorias.map(toCategoriaOption),
  ];

  return (
    <div className="grid gap-3 rounded-lg border border-cinza-200 bg-white p-3 text-xs sm:grid-cols-2 lg:grid-cols-5">
      <label className="flex flex-col gap-1 text-cinza-500">
        Status
        <Select
          value={findOption(statusOptions, filters.status)}
          options={statusOptions}
          onChange={(option) =>
            setFilters((prev) => ({ ...prev, status: option?.value || "" }))
          }
          styles={selectStyles}
          classNamePrefix="react-select"
          isSearchable={false}
        />
      </label>
      <label className="flex flex-col gap-1 text-cinza-500">
        Fornecedor
        <Select
          value={findOption(fornecedorOptions, filters.fornecedor_empresa_id)}
          options={fornecedorOptions}
          onInputChange={(value) => setSearchFornecedor(value)}
          onChange={(option) =>
            setFilters((prev) => ({
              ...prev,
              fornecedor_empresa_id: option?.value || "",
            }))
          }
          styles={selectStyles}
          classNamePrefix="react-select"
          placeholder="Todos"
        />
      </label>
      <label className="flex flex-col gap-1 text-cinza-500">
        Categoria
        <Select
          value={findOption(categoriaOptions, filters.categoria_id)}
          options={categoriaOptions}
          onChange={(option) =>
            setFilters((prev) => ({
              ...prev,
              categoria_id: option?.value || "",
            }))
          }
          styles={selectStyles}
          classNamePrefix="react-select"
          placeholder="Todas"
        />
      </label>
      <label className="flex flex-col gap-1 text-cinza-500">
        Início
        <input
          type="date"
          value={filters.periodo_inicio}
          onChange={(event) =>
            setFilters((prev) => ({
              ...prev,
              periodo_inicio: event.target.value,
            }))
          }
          className="rounded-lg border border-cinza-200 bg-white px-2 py-2 text-cinza-800 outline-none focus:border-azul-400"
        />
      </label>
      <label className="flex flex-col gap-1 text-cinza-500">
        Fim
        <input
          type="date"
          value={filters.periodo_fim}
          onChange={(event) =>
            setFilters((prev) => ({
              ...prev,
              periodo_fim: event.target.value,
            }))
          }
          className="rounded-lg border border-cinza-200 bg-white px-2 py-2 text-cinza-800 outline-none focus:border-azul-400"
        />
      </label>
    </div>
  );
}

function Dashboard({
  dashboard,
  lancamentos,
  onOpenLancamentos,
  onSaveOrcamento,
  isSavingOrcamento,
}: {
  dashboard?: DeskProducaoDashboard;
  lancamentos: DeskProducaoLancamento[];
  onOpenLancamentos: () => void;
  onSaveOrcamento: (payload: {
    valor: string;
    incentivo_percentual: string;
  }) => Promise<unknown>;
  isSavingOrcamento: boolean;
}) {
  const [orcamento, setOrcamento] = useState("");
  const [incentivoPercentual, setIncentivoPercentual] = useState("0");
  const categoriaData: DeskProducaoResumoItem[] =
    dashboard?.custos_por_categoria || [];
  const fluxoData = dashboard?.fluxo_pagamentos || [];
  const metrics = [
    {
      label: "Custo operacional",
      value: money(dashboard?.custo_total_producao),
      helper: percent(dashboard?.percentual_orcamento_comprometido),
      className: "border-azul-400/20 bg-azul-400/5",
    },
    {
      label: "Valor pago",
      value: money(dashboard?.valor_pago),
      helper: "Realizado",
      className: "border-verde-300/30 bg-verde-300/10",
    },
    {
      label: "A pagar",
      value: money(dashboard?.valor_a_pagar),
      helper: "Previsto",
      className: "border-goon-amareloClaro bg-goon-amareloClaro/30",
    },
    {
      label: "CRL",
      value: percent(dashboard?.crl, 2),
      helper: `${percent(dashboard?.percentual_custo_crl, 2)} em custos totais`,
      className: "border-cinza-200 bg-white",
    },
  ];
  const comprometido = Math.min(
    Number(dashboard?.percentual_orcamento_comprometido || 0),
    100,
  );

  useEffect(() => {
    setOrcamento(String(dashboard?.valor_proposta || ""));
    setIncentivoPercentual(String(dashboard?.incentivo_percentual || 0));
  }, [dashboard?.incentivo_percentual, dashboard?.valor_proposta]);

  async function handleSaveOrcamento(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onSaveOrcamento({
      valor: orcamento,
      incentivo_percentual: incentivoPercentual,
    });
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[1.25fr_2fr]">
        <div className="rounded-lg bg-cinza-800 p-5 text-white">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-medium uppercase text-white/60">
                Orçamento do projeto
              </p>
              <p className="mt-2 text-3xl font-semibold leading-tight">
                {money(dashboard?.valor_proposta)}
              </p>
            </div>
          </div>
          <form
            onSubmit={handleSaveOrcamento}
            className="mt-4 grid gap-3 sm:grid-cols-2"
          >
            <CurrencyInput
              label="Orçamento da Desk"
              required
              value={orcamento}
              onChange={setOrcamento}
            />
            <label className="text-xs text-white/70">
              Incentivo da agência (%) *
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                required
                value={incentivoPercentual}
                onChange={(event) => setIncentivoPercentual(event.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-white/20 bg-white px-3 text-sm text-cinza-800 outline-none transition focus:border-azul-400"
              />
            </label>
            <button
              type="submit"
              disabled={isSavingOrcamento}
              className="self-end rounded-lg bg-azul-400 px-4 py-2 text-xs font-medium text-white transition hover:bg-azul-400/90 disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
            >
              {isSavingOrcamento ? "Salvando..." : "Salvar"}
            </button>
          </form>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-white/10 p-3">
              <p className="text-white/60">Valor vendido</p>
              <p className="mt-1 font-semibold">{money(dashboard?.valor_vendido)}</p>
            </div>
            <div className="rounded-lg bg-white/10 p-3">
              <p className="text-white/60">Imposto fixo</p>
              <p className="mt-1 font-semibold">
                {percent(dashboard?.imposto_percentual)} · {money(dashboard?.valor_imposto)}
              </p>
            </div>
          </div>
          <div className="mt-5 space-y-2">
            <div className="flex items-center justify-between text-xs text-white/70">
              <span>Comprometido</span>
              <span>
                {percent(dashboard?.percentual_orcamento_comprometido)}
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/15">
              <div
                className="h-full rounded-full bg-azul-400"
                style={{ width: `${comprometido}%` }}
              />
            </div>
          </div>
          <div className="mt-5 rounded-lg bg-white/10 p-3">
            <p className="text-xs text-white/60">Saldo disponível</p>
            <p className="text-xl font-semibold">
              {money(dashboard?.saldo_disponivel)}
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className={`rounded-lg border p-4 ${metric.className}`}
            >
              <p className="text-xs font-medium text-cinza-500">
                {metric.label}
              </p>
              <p className="mt-2 break-words text-xl font-semibold text-cinza-800">
                {metric.value}
              </p>
              <p className="mt-1 text-[11px] text-cinza-500">{metric.helper}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="rounded-lg border border-cinza-200 bg-white p-4 lg:col-span-3">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-cinza-800">
              Lançamentos recentes
            </p>
            <button
              type="button"
              onClick={onOpenLancamentos}
              className="text-xs font-medium text-azul-400"
            >
              Ver todos
            </button>
          </div>
          <LancamentosTable lancamentos={lancamentos.slice(0, 5)} compact />
        </div>
        <div className="rounded-lg border border-cinza-200 bg-white p-4 lg:col-span-2">
          <p className="mb-3 text-sm font-semibold text-cinza-800">
            Fluxo de pagamentos
          </p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fluxoData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="data" tickFormatter={dateLabel} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(value) => money(Number(value))} />
                <Bar dataKey="previsto" fill="#D2D2D2" name="Previsto" />
                <Bar dataKey="pago" fill="#153DCE" name="Pago" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="rounded-lg border border-cinza-200 bg-white p-4 lg:col-span-3">
          <p className="mb-3 text-sm font-semibold text-cinza-800">
            Resumo por categoria
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoriaData}
                    dataKey="total"
                    nameKey="nome"
                    innerRadius={55}
                    outerRadius={90}
                  >
                    {categoriaData.map((item, index) => (
                      <Cell
                        key={item.id}
                        fill={
                          item.cor || CHART_COLORS[index % CHART_COLORS.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => money(Number(value))} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {categoriaData.map((item, index) => (
                <div key={item.id} className="flex items-center gap-2 text-xs">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{
                      backgroundColor:
                        item.cor || CHART_COLORS[index % CHART_COLORS.length],
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate text-cinza-700">
                    {item.nome}
                  </span>
                  <span className="text-cinza-500">{money(item.total)}</span>
                  <span className="w-12 text-right text-cinza-400">
                    {percent(item.percentual)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-cinza-200 bg-white p-4 lg:col-span-2">
          <p className="mb-3 text-sm font-semibold text-cinza-800">
            Fornecedores top
          </p>
          <FornecedoresPanel
            fornecedores={dashboard?.custos_por_fornecedor || []}
            compact
          />
        </div>
      </div>
    </div>
  );
}

function LancamentosTable({
  lancamentos,
  compact = false,
  onEdit,
  onDelete,
  onPay,
}: {
  lancamentos: DeskProducaoLancamento[];
  compact?: boolean;
  onEdit?: (lancamento: DeskProducaoLancamento) => void;
  onDelete?: (id: number) => void;
  onPay?: (lancamento: DeskProducaoLancamento) => void;
}) {
  if (!lancamentos.length) {
    return (
      <div className="rounded-lg border border-dashed border-cinza-200 p-6 text-center text-sm text-cinza-500">
        Nenhum lançamento encontrado.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-xs">
        <thead className="text-cinza-500">
          <tr className="border-b border-cinza-200">
            <th className="py-2 font-medium">Vencimento</th>
            <th className="py-2 font-medium">Fornecedor</th>
            <th className="py-2 font-medium">Descrição</th>
            <th className="py-2 font-medium">Categoria</th>
            <th className="py-2 text-right font-medium">Valor</th>
            <th className="py-2 font-medium">Status</th>
            <th className="py-2 font-medium">Arquivos</th>
            {!compact && <th className="py-2 text-right font-medium">Ações</th>}
          </tr>
        </thead>
        <tbody>
          {lancamentos.map((lancamento) => {
            const canPay =
              lancamento.status !== "pago" && lancamento.status !== "cancelado";

            return (
              <tr key={lancamento.id} className="border-b border-cinza-100">
                <td className="py-2 text-cinza-700">
                  {dateLabel(lancamento.data_vencimento)}
                </td>
                <td className="py-2 text-cinza-800">
                  {fornecedorName(lancamento.fornecedor)}
                </td>
                <td className="max-w-[220px] truncate py-2 text-cinza-700">
                  {lancamento.descricao}
                </td>
                <td className="py-2 text-cinza-700">
                  {lancamento.categoria?.nome || "-"}
                </td>
                <td className="py-2 text-right font-medium text-cinza-800">
                  {money(effectiveValue(lancamento))}
                </td>
                <td className="py-2">
                  <span
                    className={`rounded-full px-2 py-1 text-[11px] font-medium ${
                      STATUS_CLASS[lancamento.status]
                    }`}
                  >
                    {STATUS_LABELS[lancamento.status]}
                  </span>
                </td>
                <td className="py-2">
                  <AnexosResumo lancamento={lancamento} />
                </td>
                {!compact && (
                  <td className="py-2">
                    <div className="flex justify-end gap-2">
                      {onEdit && (
                        <button
                          type="button"
                          onClick={() => onEdit(lancamento)}
                          className="rounded-lg border border-cinza-200 px-2 py-1 text-cinza-600 hover:bg-cinza-100"
                        >
                          Editar
                        </button>
                      )}
                      {onPay && canPay && (
                        <button
                          type="button"
                          onClick={() => onPay(lancamento)}
                          className="rounded-lg border border-cinza-200 px-2 py-1 text-azul-400 hover:bg-azul-400/10"
                        >
                          Pagar
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(lancamento.id)}
                          className="rounded-lg border border-red-100 px-2 py-1 text-red-600 hover:bg-red-50"
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PagamentosTable({
  pagamentos,
  onPay,
}: {
  pagamentos: DeskProducaoLancamento[];
  onPay: (lancamento: DeskProducaoLancamento) => void;
}) {
  return (
    <div className="rounded-lg border border-cinza-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <Banknote className="h-4 w-4 text-azul-400" />
        <p className="text-sm font-semibold text-cinza-800">Pagamentos</p>
      </div>
      <LancamentosTable lancamentos={pagamentos} onPay={onPay} />
    </div>
  );
}

function FornecedoresPanel({
  fornecedores,
  compact = false,
  onView,
  onEdit,
  onDelete,
}: {
  fornecedores: Array<
    FornecedorOption & {
      total?: number;
      percentual?: number;
      quantidade_lancamentos?: number;
    }
  >;
  compact?: boolean;
  onView?: (fornecedor: FornecedorOption) => void;
  onEdit?: (fornecedor: FornecedorOption) => void;
  onDelete?: (fornecedor: FornecedorListItem) => void;
}) {
  if (!fornecedores.length) {
    return (
      <div className="rounded-lg border border-dashed border-cinza-200 p-6 text-center text-sm text-cinza-500">
        {compact
          ? "Nenhum fornecedor vinculado a lançamentos."
          : "Nenhum fornecedor cadastrado."}
      </div>
    );
  }

  return (
    <div
      className={
        compact
          ? "space-y-2"
          : "rounded-lg border border-cinza-200 bg-white p-4"
      }
    >
      {!compact && (
        <p className="mb-3 text-sm font-semibold text-cinza-800">
          Fornecedores cadastrados
        </p>
      )}
      <div className="space-y-2">
        {fornecedores.map((fornecedor) => (
          <div
            key={fornecedor.id}
            className="flex items-center justify-between gap-3 rounded-lg bg-cinza-100 px-3 py-2 text-xs"
          >
            <div className="min-w-0">
              <p className="truncate font-medium text-cinza-800">
                {fornecedorName(fornecedor)}
              </p>
              <p className="truncate text-cinza-500">
                {fornecedor.documento || fornecedor.tipo_servico || "-"}
              </p>
              {!compact && (
                <p
                  className={`mt-1 text-[11px] font-medium ${
                    fornecedor.quantidade_lancamentos
                      ? "text-azul-400"
                      : "text-verde-300"
                  }`}
                >
                  {fornecedor.quantidade_lancamentos
                    ? `${fornecedor.quantidade_lancamentos} lançamento(s) nesta Desk`
                    : "Sem lançamentos nesta Desk"}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <div className="text-right">
                <p className="font-semibold text-cinza-800">
                  {money(fornecedor.total || 0)}
                </p>
                {fornecedor.percentual !== undefined && (
                  <p className="text-cinza-400">
                    {percent(fornecedor.percentual)}
                  </p>
                )}
              </div>
              {onView && onEdit && (
                <div className="flex items-center gap-1 border-l border-cinza-200 pl-2">
                  <button
                    type="button"
                    onClick={() => onView(fornecedor)}
                    aria-label={`Visualizar ${fornecedorName(fornecedor)}`}
                    title="Visualizar fornecedor"
                    className="grid h-8 w-8 place-items-center rounded-lg text-cinza-500 transition-colors hover:bg-white hover:text-azul-400 focus:outline-none focus:ring-2 focus:ring-azul-400/30"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onEdit(fornecedor)}
                    aria-label={`Editar ${fornecedorName(fornecedor)}`}
                    title="Editar fornecedor"
                    className="grid h-8 w-8 place-items-center rounded-lg text-cinza-500 transition-colors hover:bg-white hover:text-azul-400 focus:outline-none focus:ring-2 focus:ring-azul-400/30"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  {onDelete && (
                    <button
                      type="button"
                      onClick={() =>
                        onDelete(fornecedor as FornecedorListItem)
                      }
                      aria-label={`Remover ${fornecedorName(fornecedor)}`}
                      title={
                        fornecedor.quantidade_lancamentos
                          ? "Fornecedor possui lançamentos nesta Desk"
                          : "Remover fornecedor"
                      }
                      className={`grid h-8 w-8 place-items-center rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-300/40 ${
                        fornecedor.quantidade_lancamentos
                          ? "text-cinza-300 hover:bg-white hover:text-azul-400"
                          : "text-cinza-500 hover:bg-red-50 hover:text-red-600"
                      }`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoriasPanel({
  categorias,
  onCreate,
}: {
  categorias: DeskProducaoCategoria[];
  onCreate: () => void;
}) {
  return (
    <div className="rounded-lg border border-cinza-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-cinza-800">Categorias</p>
        <button
          type="button"
          onClick={onCreate}
          className="rounded-lg bg-azul-400 px-3 py-2 text-xs font-medium text-white"
        >
          Nova categoria
        </button>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {categorias.map((categoria) => (
          <div
            key={categoria.id}
            className="flex items-center justify-between rounded-lg border border-cinza-200 px-3 py-2 text-sm"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: categoria.cor || "#153DCE" }}
              />
              <span className="truncate text-cinza-800">{categoria.nome}</span>
            </div>
            <span className="text-xs text-cinza-400">
              {categoria.ativo ? "Ativa" : "Inativa"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LancamentoModal({
  deskId,
  open,
  lancamento,
  categorias,
  fornecedores,
  setSearchFornecedor,
  onClose,
  onSaved,
}: {
  deskId: number;
  open: boolean;
  lancamento?: DeskProducaoLancamento | null;
  categorias: DeskProducaoCategoria[];
  fornecedores: FornecedorOption[];
  setSearchFornecedor: (value: string) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<LancamentoFormState>(() =>
    getLancamentoForm(lancamento),
  );
  const [files, setFiles] = useState<FileList | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(getLancamentoForm(lancamento));
      setFiles(null);
    }
  }, [open, lancamento]);

  function update(key: keyof LancamentoFormState, value: string | boolean) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function createFornecedorInline() {
    const fornecedorData = new FormData();
    fornecedorData.append("nome", form.fornecedor_nome);
    fornecedorData.append(
      "name_fantasy",
      form.fornecedor_name_fantasy || form.fornecedor_nome,
    );
    fornecedorData.append("tipo", "PJ");
    if (form.fornecedor_documento) {
      fornecedorData.append("documento", form.fornecedor_documento);
    }
    if (form.fornecedor_email) {
      fornecedorData.append("email", form.fornecedor_email);
    }
    if (form.fornecedor_telefone) {
      fornecedorData.append("telefone", form.fornecedor_telefone);
    }
    if (form.fornecedor_tipo_servico) {
      fornecedorData.append("tipo_servico", form.fornecedor_tipo_servico);
    }

    const response =
      await FornecedoresEmpresaServices.cadastrarFornecedor(fornecedorData);
    if (response?.status !== 200) {
      throw new Error(response?.data?.error || "Erro ao criar fornecedor");
    }
    return response.data?.fornecedor?.id;
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form.novo_fornecedor && !form.fornecedor_empresa_id) {
      toast.error("Selecione um fornecedor");
      return;
    }
    if (form.novo_fornecedor && !form.fornecedor_nome.trim()) {
      toast.error("Informe o nome do fornecedor");
      return;
    }

    setIsSubmitting(true);
    try {
      const fornecedorId = form.novo_fornecedor
        ? await createFornecedorInline()
        : form.fornecedor_empresa_id;
      const payload = buildFormData(
        {
          fornecedor_empresa_id: fornecedorId,
          categoria_id: form.categoria_id,
          descricao: form.descricao,
          centro_custo: form.centro_custo,
          departamento_responsavel: form.departamento_responsavel,
          data_contratacao: form.data_contratacao,
          data_vencimento: form.data_vencimento,
          data_pagamento: form.data_pagamento,
          valor_orcado: form.valor_orcado,
          valor_aprovado: form.valor_aprovado,
          valor_final: form.valor_final,
          forma_pagamento: form.forma_pagamento,
          parcelamento: form.parcelamento,
          numero_documento: form.numero_documento,
          status: form.status,
          observacoes: form.observacoes,
        },
        files,
      );
      const response = lancamento
        ? await ServiceDeskProducao.updateLancamento(
            deskId,
            lancamento.id,
            payload,
          )
        : await ServiceDeskProducao.createLancamento(deskId, payload);

      if (response?.status === 200) {
        toast.success(response.data?.msg || "Lançamento salvo");
        onSaved();
        onClose();
      } else {
        toast.error(response?.data?.msg || "Erro ao salvar lançamento");
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao salvar lançamento"));
    } finally {
      setIsSubmitting(false);
    }
  }

  const fornecedorOptions = fornecedores.map(toFornecedorOption);
  const categoriaOptions = categorias
    .filter((categoria) => categoria.ativo)
    .map(toCategoriaOption);
  const fornecedorCategoriaOptions = categorias
    .filter((categoria) => categoria.ativo)
    .map((categoria) => ({
      value: categoria.nome,
      label: categoria.nome,
    }));
  const statusOptions = Object.entries(STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  return (
    <ModalBase
      open={open}
      onOpenChange={(nextOpen) => !nextOpen && onClose()}
      className="w-[min(94vw,860px)] rounded-lg bg-white p-0"
      title={lancamento ? "Editar lançamento" : "Novo lançamento"}
    >
      <form onSubmit={submit} className="max-h-[84vh] overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-cinza-200 bg-white px-5 py-4">
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold text-cinza-800">
              {lancamento ? "Editar lançamento" : "Novo lançamento"}
            </p>
            <p className="text-xs text-cinza-500">
              Informe fornecedor, valor e vencimento para controlar a produção.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-cinza-200 px-3 py-2 text-xs text-cinza-600"
          >
            Fechar
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <section className="rounded-lg border border-cinza-200 bg-cinza-100/70 p-3">
            <label className="mb-3 flex items-center gap-2 text-sm font-medium text-cinza-800">
              <input
                type="checkbox"
                checked={form.novo_fornecedor}
                onChange={(event) =>
                  update("novo_fornecedor", event.target.checked)
                }
              />
              Criar novo fornecedor neste lançamento
            </label>

            {form.novo_fornecedor ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Input
                  label="Nome / Razão social"
                  required
                  value={form.fornecedor_nome}
                  onChange={(value) => update("fornecedor_nome", value)}
                />
                <Input
                  label="Nome fantasia"
                  required
                  value={form.fornecedor_name_fantasy}
                  onChange={(value) => update("fornecedor_name_fantasy", value)}
                />
                <Input
                  label="CPF/CNPJ"
                  value={form.fornecedor_documento}
                  onChange={(value) =>
                    update("fornecedor_documento", formatCpfCnpj(value))
                  }
                  maxLength={18}
                  inputMode="numeric"
                />
                <Input
                  label="E-mail"
                  type="email"
                  value={form.fornecedor_email}
                  onChange={(value) => update("fornecedor_email", value)}
                />
                <Input
                  label="Telefone"
                  value={form.fornecedor_telefone}
                  onChange={(value) =>
                    update("fornecedor_telefone", formatBrazilPhone(value))
                  }
                  maxLength={15}
                  inputMode="numeric"
                />
                <label className="flex flex-col gap-1 text-xs text-cinza-500">
                  Categoria/tipo do fornecedor
                  <Select
                    value={findOption(
                      fornecedorCategoriaOptions,
                      form.fornecedor_tipo_servico,
                    )}
                    options={fornecedorCategoriaOptions}
                    onChange={(option) =>
                      update("fornecedor_tipo_servico", option?.value || "")
                    }
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    placeholder="Selecione"
                  />
                </label>
              </div>
            ) : (
              <label className="flex flex-col gap-1 text-xs text-cinza-500">
                <span className="flex items-center gap-2">
                  <Search className="h-3.5 w-3.5" />
                  <RequiredLabel required>Fornecedor</RequiredLabel>
                </span>
                <Select
                  value={findOption(
                    fornecedorOptions,
                    form.fornecedor_empresa_id,
                  )}
                  options={fornecedorOptions}
                  onInputChange={(value) => setSearchFornecedor(value)}
                  onChange={(option) =>
                    update("fornecedor_empresa_id", option?.value || "")
                  }
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  placeholder="Buscar ou selecionar fornecedor"
                />
              </label>
            )}
          </section>

          <section className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Descrição"
              required
              value={form.descricao}
              onChange={(value) => update("descricao", value)}
            />
            <label className="flex flex-col gap-1 text-xs text-cinza-500">
              <RequiredLabel required>Categoria</RequiredLabel>
              <Select
                value={findOption(categoriaOptions, form.categoria_id)}
                options={categoriaOptions}
                onChange={(option) =>
                  update("categoria_id", option?.value || "")
                }
                styles={selectStyles}
                classNamePrefix="react-select"
                placeholder="Selecione"
              />
            </label>
            <Input
              label="Centro de custo"
              value={form.centro_custo}
              onChange={(value) => update("centro_custo", value)}
            />
            <Input
              label="Departamento/responsável"
              value={form.departamento_responsavel}
              onChange={(value) => update("departamento_responsavel", value)}
            />
          </section>

          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Input
              label="Contratação"
              type="date"
              value={form.data_contratacao}
              onChange={(value) => update("data_contratacao", value)}
            />
            <Input
              label="Vencimento"
              type="date"
              required
              value={form.data_vencimento}
              onChange={(value) => update("data_vencimento", value)}
            />
            <Input
              label="Pagamento"
              type="date"
              value={form.data_pagamento}
              onChange={(value) => update("data_pagamento", value)}
            />
            <CurrencyInput
              label="Valor orçado"
              required
              value={form.valor_orcado}
              onChange={(value) => update("valor_orcado", value)}
            />
            <CurrencyInput
              label="Valor aprovado"
              value={form.valor_aprovado}
              onChange={(value) => update("valor_aprovado", value)}
            />
            <CurrencyInput
              label="Valor final/pago"
              value={form.valor_final}
              onChange={(value) => update("valor_final", value)}
            />
            <Input
              label="Forma de pagamento"
              value={form.forma_pagamento}
              onChange={(value) => update("forma_pagamento", value)}
            />
            <Input
              label="Parcelamento"
              value={form.parcelamento}
              onChange={(value) => update("parcelamento", value)}
            />
            <Input
              label="Nota/documento"
              value={form.numero_documento}
              onChange={(value) => update("numero_documento", value)}
            />
            <label className="flex flex-col gap-1 text-xs text-cinza-500">
              <RequiredLabel required>Status</RequiredLabel>
              <Select
                value={findOption(statusOptions, form.status)}
                options={statusOptions}
                onChange={(option) =>
                  update(
                    "status",
                    (option?.value || "orcado") as DeskProducaoStatus,
                  )
                }
                styles={selectStyles}
                classNamePrefix="react-select"
                isSearchable={false}
              />
            </label>
          </section>

          <section className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs text-cinza-500">
              <span className="flex items-center gap-2">
                <Paperclip className="h-3.5 w-3.5" />
                Orçamento, NF ou comprovante
              </span>
              <input
                type="file"
                multiple
                onChange={(event) => setFiles(event.target.files)}
                className="rounded-lg border border-cinza-200 px-3 py-2 text-sm text-cinza-800"
              />
            </label>
            <RichTextArea
              label="Observações"
              value={form.observacoes}
              onChange={(value) => update("observacoes", value)}
            />
          </section>
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-cinza-200 bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-cinza-200 px-4 py-2 text-sm text-cinza-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-lg bg-azul-400 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {isSubmitting ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}

function PagamentoModal({
  deskId,
  lancamento,
  lancamentos,
  open,
  onClose,
  onOpenChange,
  onSaved,
}: {
  deskId: number;
  lancamento: DeskProducaoLancamento | null;
  lancamentos: DeskProducaoLancamento[];
  open: boolean;
  onClose: () => void;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [dataPagamento, setDataPagamento] = useState(
    dateInput(new Date().toISOString()),
  );
  const [valorFinal, setValorFinal] = useState("");
  const [selectedLancamentoId, setSelectedLancamentoId] = useState("");
  const [files, setFiles] = useState<FileList | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const payableLancamentos = useMemo(
    () =>
      lancamentos.filter(
        (item) => item.status !== "pago" && item.status !== "cancelado",
      ),
    [lancamentos],
  );
  const lancamentoSelecionado =
    lancamento ||
    payableLancamentos.find(
      (item) => String(item.id) === selectedLancamentoId,
    ) ||
    null;
  const lancamentoOptions = payableLancamentos.map((item) => ({
    value: String(item.id),
    label: `${item.descricao} - ${fornecedorName(item.fornecedor)} - ${money(
      effectiveValue(item),
    )}`,
  }));

  useEffect(() => {
    if (open) {
      const current = lancamento || payableLancamentos[0] || null;
      setSelectedLancamentoId(current ? String(current.id) : "");
      setDataPagamento(
        dateInput(current?.data_pagamento || new Date().toISOString()),
      );
      setValorFinal(
        current ? String(current.valor_final ?? effectiveValue(current)) : "",
      );
      setFiles(null);
    }
  }, [open, lancamento, payableLancamentos]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!lancamentoSelecionado) {
      toast.error("Selecione um lançamento pendente");
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = buildFormData({
        pago: true,
        data_pagamento: dataPagamento,
        valor_final: valorFinal,
      });
      Array.from(files || []).forEach((file) =>
        payload.append("comprovantes", file),
      );
      const response = await ServiceDeskProducao.marcarPagamento(
        deskId,
        lancamentoSelecionado.id,
        payload,
      );
      if (response?.status === 200) {
        toast.success(response.data?.msg || "Pagamento registrado");
        onSaved();
        onOpenChange(false);
        onClose();
      } else {
        toast.error(response?.data?.msg || "Erro ao registrar pagamento");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ModalBase
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) onClose();
      }}
      className="w-[min(94vw,520px)] rounded-lg bg-white p-5"
      title="Registrar pagamento"
    >
      <form onSubmit={submit} className="space-y-3">
        <p className="text-lg font-semibold text-cinza-800">
          Registrar pagamento
        </p>
        {!lancamento && (
          <label className="flex flex-col gap-1 text-xs text-cinza-500">
            <RequiredLabel required>Lançamento</RequiredLabel>
            <Select
              value={findOption(lancamentoOptions, selectedLancamentoId)}
              options={lancamentoOptions}
              onChange={(option) => {
                const next = option?.value || "";
                const selected = payableLancamentos.find(
                  (item) => String(item.id) === next,
                );
                setSelectedLancamentoId(next);
                setValorFinal(
                  selected
                    ? String(selected.valor_final ?? effectiveValue(selected))
                    : "",
                );
              }}
              styles={selectStyles}
              classNamePrefix="react-select"
              placeholder="Selecione um lançamento pendente"
            />
          </label>
        )}
        {lancamentoSelecionado && (
          <p className="rounded-lg bg-cinza-100 px-3 py-2 text-sm text-cinza-700">
            {lancamentoSelecionado.descricao}
          </p>
        )}
        <Input
          label="Data de pagamento"
          type="date"
          required
          value={dataPagamento}
          onChange={setDataPagamento}
        />
        <CurrencyInput
          label="Valor final/pago"
          required
          value={valorFinal}
          onChange={setValorFinal}
        />
        <label className="flex flex-col gap-1 text-xs text-cinza-500">
          Comprovante
          <input
            type="file"
            multiple
            onChange={(event) => setFiles(event.target.files)}
            className="rounded-lg border border-cinza-200 px-3 py-2 text-sm text-cinza-800"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => {
              onOpenChange(false);
              onClose();
            }}
            className="rounded-lg border border-cinza-200 px-4 py-2 text-sm text-cinza-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2 rounded-lg bg-azul-400 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            <CheckCircle2 className="h-4 w-4" />
            {isSubmitting ? "Salvando..." : "Confirmar"}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}

function FornecedorModal({
  open,
  mode,
  fornecedorId,
  onModeChange,
  onClose,
  onSaved,
  categorias,
}: {
  open: boolean;
  mode: FornecedorModalMode;
  fornecedorId?: number;
  onModeChange: (mode: FornecedorModalMode) => void;
  onClose: () => void;
  onSaved: () => void;
  categorias: DeskProducaoCategoria[];
}) {
  const [form, setForm] = useState<FornecedorFormState>(emptyFornecedorForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReadOnly = mode === "view";
  const isEditing = mode === "edit";
  const fornecedorQuery = useQuery({
    queryKey: ["fornecedor-empresa", fornecedorId],
    queryFn: () =>
      FornecedoresEmpresaServices.getFornecedor(fornecedorId as number),
    enabled: open && mode !== "create" && Boolean(fornecedorId),
    retry: 1,
  });
  const fornecedor = (fornecedorQuery.data?.data?.data ||
    null) as FornecedorDetalhe | null;
  const hasLoadError =
    mode !== "create" &&
    !fornecedorQuery.isLoading &&
    (fornecedorQuery.isError ||
      !fornecedorQuery.data ||
      fornecedorQuery.data.status !== 200);
  const categoriaOptions = categorias
    .filter((categoria) => categoria.ativo)
    .map((categoria) => ({
      value: categoria.nome,
      label: categoria.nome,
    }));

  useEffect(() => {
    if (!open) return;

    if (mode === "create") {
      setForm(emptyFornecedorForm);
      return;
    }

    if (fornecedor) {
      setForm({
        nome: fornecedor.nome || "",
        name_fantasy: fornecedor.name_fantasy || "",
        tipo: fornecedor.tipo === "PF" ? "PF" : "PJ",
        documento: formatCpfCnpj(fornecedor.documento || ""),
        email: fornecedor.email || "",
        telefone: formatBrazilPhone(fornecedor.telefone || ""),
        tipo_servico: fornecedor.tipo_servico || "",
        website: fornecedor.website || "",
        cep: fornecedor.cep || "",
        endereco: fornecedor.endereco || "",
        numero: fornecedor.numero ? String(fornecedor.numero) : "",
        complemento: fornecedor.complemento || "",
        bairro: fornecedor.bairro || "",
        municipio: fornecedor.municipio || "",
        uf: fornecedor.uf || "",
        descricao: fornecedor.descricao || "",
      });
    }
  }, [fornecedor, mode, open]);

  function update(key: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form.nome.trim()) {
      toast.error("Informe o nome do fornecedor");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("tipo", form.tipo);
      Object.entries(form).forEach(([key, value]) => {
        if (key !== "tipo") formData.append(key, value);
      });

      const response = isEditing
        ? await FornecedoresEmpresaServices.editarFornecedor(
            formData,
            fornecedorId,
          )
        : await FornecedoresEmpresaServices.cadastrarFornecedor(formData);
      if (response?.status === 200) {
        toast.success(
          isEditing
            ? "Fornecedor atualizado com sucesso"
            : "Fornecedor criado com sucesso",
        );
        onSaved();
        onClose();
      } else if (!isEditing) {
        toast.error(
          response?.data?.error ||
            response?.data?.msg ||
            "Erro ao criar fornecedor",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ModalBase
      open={open}
      onOpenChange={(nextOpen) => !nextOpen && onClose()}
      className="w-[min(96vw,920px)] rounded-lg bg-white p-0"
      title={
        mode === "create"
          ? "Adicionar fornecedor"
          : mode === "edit"
            ? "Editar fornecedor"
            : "Visualizar fornecedor"
      }
    >
      <form onSubmit={submit}>
        <div className="flex items-center justify-between gap-3 border-b border-cinza-200 px-5 py-4">
          <div>
            <p className="text-lg font-semibold text-cinza-800">
              {mode === "create"
                ? "Adicionar fornecedor"
                : mode === "edit"
                  ? "Editar fornecedor"
                  : "Dados do fornecedor"}
            </p>
            <p className="text-xs text-cinza-500">
              {mode === "create"
                ? "O fornecedor ficará disponível para outros projetos."
                : "Cadastro compartilhado entre os projetos da empresa."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-cinza-200 px-3 py-2 text-xs text-cinza-600"
          >
            Fechar
          </button>
        </div>

        {fornecedorQuery.isLoading && mode !== "create" ? (
          <div className="grid min-h-52 place-items-center px-5 py-8 text-sm text-cinza-500">
            Carregando dados do fornecedor...
          </div>
        ) : hasLoadError ? (
          <div className="grid min-h-52 place-items-center gap-3 px-5 py-8 text-center">
            <div>
              <p className="font-medium text-cinza-800">
                Não foi possível carregar este fornecedor.
              </p>
              <p className="mt-1 text-sm text-cinza-500">
                Verifique sua permissão ou tente novamente.
              </p>
            </div>
            <button
              type="button"
              onClick={() => fornecedorQuery.refetch()}
              className="rounded-lg bg-azul-400 px-4 py-2 text-sm font-medium text-white"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <div className="grid max-h-[70vh] gap-3 overflow-y-auto px-5 py-4 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            label="Nome / Razão social"
            required
            value={form.nome}
            onChange={(value) => update("nome", value)}
            disabled={isReadOnly}
          />
          <Input
            label="Nome fantasia"
            value={form.name_fantasy}
            onChange={(value) => update("name_fantasy", value)}
            disabled={isReadOnly}
          />
          <label className="flex flex-col gap-1 text-xs text-cinza-500">
            <RequiredLabel required>Tipo</RequiredLabel>
            <Select<SelectOption, false>
              value={
                form.tipo === "PF"
                  ? { value: "PF", label: "Pessoa física" }
                  : { value: "PJ", label: "Pessoa jurídica" }
              }
              options={[
                { value: "PJ", label: "Pessoa jurídica" },
                { value: "PF", label: "Pessoa física" },
              ]}
              onChange={(option) =>
                update("tipo", option?.value === "PF" ? "PF" : "PJ")
              }
              isDisabled={isReadOnly}
              styles={selectStyles}
            />
          </label>
          <Input
            label="CPF/CNPJ"
            value={form.documento}
            onChange={(value) => update("documento", formatCpfCnpj(value))}
            maxLength={18}
            inputMode="numeric"
            disabled={isReadOnly}
          />
          <Input
            label="E-mail"
            type="email"
            value={form.email}
            onChange={(value) => update("email", value)}
            disabled={isReadOnly}
          />
          <Input
            label="Telefone"
            value={form.telefone}
            onChange={(value) => update("telefone", formatBrazilPhone(value))}
            maxLength={15}
            inputMode="numeric"
            disabled={isReadOnly}
          />
          <label className="flex flex-col gap-1 text-xs text-cinza-500">
            Categoria/tipo do fornecedor
            <Select
              value={findOption(categoriaOptions, form.tipo_servico)}
              options={categoriaOptions}
              onChange={(option) =>
                update("tipo_servico", option?.value || "")
              }
              isDisabled={isReadOnly}
              styles={selectStyles}
              classNamePrefix="react-select"
              placeholder="Selecione"
            />
          </label>
          <Input
            label="Website"
            value={form.website}
            onChange={(value) => update("website", value)}
            disabled={isReadOnly}
          />
          <Input
            label="CEP"
            value={form.cep}
            onChange={(value) => update("cep", value)}
            maxLength={10}
            inputMode="numeric"
            disabled={isReadOnly}
          />
          <Input
            label="Endereço"
            value={form.endereco}
            onChange={(value) => update("endereco", value)}
            disabled={isReadOnly}
          />
          <Input
            label="Número"
            value={form.numero}
            onChange={(value) => update("numero", onlyDigits(value, 10))}
            inputMode="numeric"
            disabled={isReadOnly}
          />
          <Input
            label="Complemento"
            value={form.complemento}
            onChange={(value) => update("complemento", value)}
            disabled={isReadOnly}
          />
          <Input
            label="Bairro"
            value={form.bairro}
            onChange={(value) => update("bairro", value)}
            disabled={isReadOnly}
          />
          <Input
            label="Cidade"
            value={form.municipio}
            onChange={(value) => update("municipio", value)}
            disabled={isReadOnly}
          />
          <Input
            label="UF"
            value={form.uf}
            onChange={(value) => update("uf", value.toUpperCase().slice(0, 2))}
            maxLength={2}
            disabled={isReadOnly}
          />
          <div className="sm:col-span-2 lg:col-span-3">
            <RichTextArea
              label="Observações"
              value={form.descricao}
              onChange={(value) => update("descricao", value)}
              readOnly={isReadOnly}
            />
          </div>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-cinza-200 px-5 py-4">
          {isReadOnly ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-cinza-200 px-4 py-2 text-sm text-cinza-700"
              >
                Fechar
              </button>
              {!hasLoadError && (
                <button
                  type="button"
                  onClick={() => onModeChange("edit")}
                  className="inline-flex items-center gap-2 rounded-lg bg-azul-400 px-4 py-2 text-sm font-medium text-white"
                >
                  <Pencil className="h-4 w-4" />
                  Editar fornecedor
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-cinza-200 px-4 py-2 text-sm text-cinza-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting || hasLoadError}
                className="rounded-lg bg-azul-400 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {isSubmitting
                  ? "Salvando..."
                  : isEditing
                    ? "Salvar alterações"
                    : "Salvar fornecedor"}
              </button>
            </>
          )}
        </div>
      </form>
    </ModalBase>
  );
}

function FornecedorDeleteModal({
  fornecedor,
  isDeleting,
  onClose,
  onConfirm,
}: {
  fornecedor: FornecedorListItem | null;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: (fornecedorId: number) => void;
}) {
  return (
    <ModalBase
      open={Boolean(fornecedor)}
      onOpenChange={(nextOpen) => !nextOpen && onClose()}
      className="w-[min(94vw,440px)] rounded-lg bg-white p-0"
      title="Remover fornecedor"
    >
      <div className="space-y-4 p-5">
        <div>
          <p className="text-lg font-semibold text-cinza-800">
            Remover fornecedor?
          </p>
          <p className="mt-2 text-sm leading-6 text-cinza-500">
            O cadastro de <strong>{fornecedorName(fornecedor)}</strong> será
            removido da empresa e deixará de ficar disponível para novos
            projetos.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg border border-cinza-200 px-4 py-2 text-sm text-cinza-700 disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => fornecedor && onConfirm(fornecedor.id)}
            disabled={!fornecedor || isDeleting}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" />
            {isDeleting ? "Removendo..." : "Remover"}
          </button>
        </div>
      </div>
    </ModalBase>
  );
}

function CategoriaModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nome, setNome] = useState("");
  const [cor, setCor] = useState("#153DCE");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await ServiceDeskProducao.createCategoria({ nome, cor });
      if (response?.status === 200) {
        toast.success("Categoria criada com sucesso");
        setNome("");
        onSaved();
        onClose();
      } else {
        toast.error(response?.data?.msg || "Erro ao criar categoria");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ModalBase
      open={open}
      onOpenChange={(nextOpen) => !nextOpen && onClose()}
      className="w-full max-w-md rounded-lg bg-white p-5"
      title="Nova categoria"
    >
      <form onSubmit={submit} className="space-y-3">
        <p className="text-lg font-semibold text-cinza-800">Nova categoria</p>
        <Input label="Nome" required value={nome} onChange={setNome} />
        <label className="flex flex-col gap-1 text-xs text-cinza-500">
          Cor
          <input
            type="color"
            value={cor}
            onChange={(event) => setCor(event.target.value)}
            className="h-10 rounded-lg border border-cinza-200 px-2"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-cinza-200 px-4 py-2 text-sm text-cinza-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-lg bg-azul-400 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {isSubmitting ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}

function CurrencyInput({
  label,
  value,
  onChange,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-cinza-500">
      <RequiredLabel required={required}>{label}</RequiredLabel>
      <Cleave
        required={required}
        value={toCurrencyInputValue(value)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onChange(normalizeCurrencyInput(event.target.value))
        }
        options={{
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
          numeralDecimalMark: ",",
          delimiter: ".",
          prefix: "R$ ",
        }}
        placeholder="R$ 0,00"
        className="rounded-lg border border-cinza-200 px-3 py-2 text-sm text-cinza-800 outline-none focus:border-azul-400"
      />
    </label>
  );
}

function RichTextArea({
  label,
  value,
  onChange,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-cinza-500">
      {label}
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        modules={readOnly ? { toolbar: false } : undefined}
        className={`min-h-[142px] bg-white [&_.ql-container]:min-h-[96px] [&_.ql-editor]:min-h-[96px] [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:rounded-b-lg ${
          readOnly ? "[&_.ql-container]:rounded-lg [&_.ql-editor]:bg-cinza-100" : ""
        }`}
      />
    </label>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  maxLength,
  inputMode,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  disabled?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-cinza-500">
      <RequiredLabel required={required}>{label}</RequiredLabel>
      <input
        type={type}
        required={required}
        min={type === "number" ? 0 : undefined}
        step={type === "number" ? "0.01" : undefined}
        maxLength={maxLength}
        inputMode={inputMode}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-cinza-200 px-3 py-2 text-sm text-cinza-800 outline-none focus:border-azul-400 disabled:cursor-default disabled:bg-cinza-100 disabled:text-cinza-600"
      />
    </label>
  );
}
